import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { take, tap } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { KeyboardShortcuts } from 'src/app/auto-cx/enums/keyboard-shortcuts.enum';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ToastService } from './shared/services/toast.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('detailExpand', [
      state('closed', style({ margin: '-100px 0 0 0' })),
      state('open', style({ margin: '0' })),
      transition('closed <=> open', animate('400ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ],
  standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

  sessionVerifiedSource$: boolean;
  initComplete$: boolean;

  // We need to wait for the session to be initialzed. This prevents
  // the page from rendering until we know we have a bearer token
  sessionInitialized: boolean;
  chatSessionActive: boolean = false;
  chatEnabled: boolean = false;
  // Shared message type used by the host portal and iframe apps for shortcut relays.
  private readonly keyboardShortcutMessageType = '75F_KEYBOARD_SHORTCUT';
  // Prevents a shortcut replayed from the parent from being immediately posted back to the parent.
  private suppressNextParentShortcutRelay = false;
  /**
   * Tracks whether keyboard tab navigation focus is active in the parent shell (topbar/sidebar).
   * Used to know whether a click inside this iframe needs to clear the parent outline.
   */
  isTabNavActive: boolean = false;
  private googleTranslatePollId?: ReturnType<typeof setInterval>;

  constructor(
    private toast: ToastService,
    private loader: LoaderService,
    private authService: AuthenticationService) {
      const currentUrl = new URL(window.location.href);
      const { searchParams } = currentUrl;
      const rawChatEnabled = (searchParams.get('chatEnabled'))?.toLowerCase();
      this.chatEnabled = rawChatEnabled === 'true';
     }

  ngOnInit(): void {

    if (this.authService.isExcludedPath()) this.authService.anonymousSession();
    else this.authorizeSession();

    // Listen for the users permissions
    this.authService.sessionVerified$.pipe(
      take(1),
      tap((response) => {
        this.sessionVerifiedSource$ = response;
        this.initComplete$ = true;
        this.sessionInitialized = true;
      })
    ).subscribe();
  }

  ngAfterViewInit(): void {
    this.loadGoogleTranslate();
  }

  ngOnDestroy(): void {
    if (this.googleTranslatePollId) {
      clearInterval(this.googleTranslatePollId);
    }
  }

  // The host portal passes the user's language as ?preferredLanguage=<code>; translate this iframe.
  loadGoogleTranslate(): void {
    const preferredLanguage = new URL(window.location.href).searchParams.get('preferredLanguage') || 'en';
    document.body.classList.toggle('notranslate', preferredLanguage === 'en');

    (window as Window & { gtranslateSettings?: Record<string, string | boolean> }).gtranslateSettings = {
      default_language: 'en',
      detect_browser_language: false,
      wrapper_selector: '.gtranslate_wrapper',
      horizontal_position: 'left',
      vertical_position: 'bottom',
      select_language_label: 'select'
    };

    const existingScript = document.getElementById('gtranslate-script');
    if (existingScript) {
      this.applyGoogleTranslateLanguage(preferredLanguage);
      return;
    }

    const script = document.createElement('script');
    script.id = 'gtranslate-script';
    script.type = 'text/javascript';
    script.src = 'https://cdn.gtranslate.net/widgets/latest/dropdown.js';
    script.onload = () => this.applyGoogleTranslateLanguage(preferredLanguage);
    script.onerror = () => script.remove();
    document.head.appendChild(script);
  }

  private applyGoogleTranslateLanguage(language: string): void {
    if (this.googleTranslatePollId) {
      clearInterval(this.googleTranslatePollId);
    }

    const startedAt = Date.now();
    this.googleTranslatePollId = setInterval(() => {
      const select = document.querySelector('.gtranslate_wrapper select') as HTMLSelectElement | null;
      if (select?.options.length) {
        const option = Array.from(select.options).find(({ value }) =>
          value === language || value.endsWith(`|${language}`)
        );
        if (option) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        clearInterval(this.googleTranslatePollId);
        this.googleTranslatePollId = undefined;
      } else if (Date.now() - startedAt > 10000) {
        clearInterval(this.googleTranslatePollId);
        this.googleTranslatePollId = undefined;
      }
    }, 100);
  }

  get loading() {
    return this.loader.isLoading;
  }

  /**
  * Get the token from the url and validate it
  */
  authorizeSession() {
    const token = this.getToken();
    if (token == null) {
      this.toast.invalidSession({message: 'No token supplied'});
      return;
    }

    this.authService.validateToken(token);
  }

  getToken() {
    return new URL(window.location.href).searchParams.get('token') || localStorage.getItem('bearer-token');
  }

  //Change triggered from parent componet(Internal/Facilisight)
  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    let messageFromParent: any = event?.data;
    if (typeof event?.data === 'string') {
      try {
        messageFromParent = JSON.parse(event.data);
      } catch {
        return;
      }
    }

    //Handle the chatSessionActive state(we are swtiching the icons absed on this)
    if (messageFromParent?.chatSessionActive !== undefined) {
      this.chatSessionActive = messageFromParent.chatSessionActive;
    }

    // Synchronize tab navigation state from parent shell (indicates whether topbar/sidebar outline is visible)
    if (messageFromParent?.type === '75F_TAB_NAV_STATE') {
      this.isTabNavActive = !!messageFromParent.active;
    }

      // Parent focus -> iframe shortcut: replay parent-owned messages as local keydown events.
    if (messageFromParent?.type === this.keyboardShortcutMessageType && messageFromParent?.source === 'PARENT') {
      this.replayShortcutFromParent(messageFromParent);
    }
  }

  /**
   * Clears the tab navigation outline on the parent topbar/sidebar when the user clicks inside the iframe.
   * Only posts a message when `isTabNavActive` is true to avoid unnecessary postMessage traffic on every click.
   */
  @HostListener('window:mousedown')
  onMousedown(): void {
    if (this.isTabNavActive) {
      this.isTabNavActive = false;
      window.parent.postMessage(JSON.stringify({ type: '75F_IFRAME_MOUSEDOWN' }), '*');
    }
  }

  //The icon is used to toggle the chat session state, so we need to notify the parent component(Internal)
  // of the change so it can update the chat session accordingly.
  //Since the chat session state is managed in the parent component, we don't need to do anything else here besides notify the parent of the change.
  onToggleChat() {
    window.parent.postMessage(JSON.stringify({ chatSessionActive: this.chatSessionActive }), '*');
  }

  /**
   * Intercepts keyboard navigation and shortcut events within the iframe and forwards them to the parent shell.
   * - Tab / Shift+Tab: Allows keyboard focus to cycle between the embedded iframe and the host shell (topbar/sidebar).
   * - Escape: Closes active host panels/drawers or clears focus.
   * - ? / Shift+/: Opens the keyboard shortcut help dialog in the host portal.
   * - S: Opens Saffron AI in the host portal.
   *
   * Form inputs, textareas, and contenteditable elements are bypassed to allow normal typing.
   */
  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Do not echo the synthetic keydown that was created from a parent shortcut message.
    if (this.suppressNextParentShortcutRelay) {
      this.suppressNextParentShortcutRelay = false;
      return;
    }
    const target = event.target as HTMLElement;
    const isInput = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

    // Do not intercept if user is typing inside a text box or form input
    if (isInput) {
      return;
    }

    const isTab = event.key === 'Tab' || event.code === 'Tab';
    const isEscape = event.key === 'Escape' || event.code === 'Escape';
    const isHelp = event.key === '?' || (event.shiftKey && (event.key === '/' || event.code === 'Slash'));
    const isChatShortcut = event.key.toLowerCase() === 's' && !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey;

      // Iframe focus -> parent shortcut: forward host-owned navigation/help shortcuts.
    if (isTab || isEscape || isHelp) {
      event.preventDefault();
      this.postShortcutToParent(event);
    } else if (isChatShortcut && this.chatEnabled && !this.chatSessionActive) {
      // Saffron/chat is owned by the host shell, so send the shortcut to parent instead of handling it here.
      event.preventDefault();
      this.postShortcutToParent(event);
    } else if (this.shouldBridgeKeyboardEvent(event)) {
      // Forward other app shortcut candidates; parent will ignore anything it does not own.
      this.postShortcutToParent(event);
    }
  }

  // Replays a parent shortcut inside CPM so existing local shortcut services can handle it normally.
  private replayShortcutFromParent(payload: any): void {
    this.suppressNextParentShortcutRelay = true;
    document.dispatchEvent(new KeyboardEvent('keydown', {
      key: payload.key,
      code: payload.code,
      shiftKey: !!payload.shiftKey,
      altKey: !!payload.altKey,
      ctrlKey: !!payload.ctrlKey,
      metaKey: !!payload.metaKey,
      bubbles: true,
      cancelable: true
    }));
  }

  // Sends an iframe keydown to the parent shell using the shared shortcut bridge message format.
  private postShortcutToParent(event: KeyboardEvent): void {
    if (window.parent === window) {
      return;
    }

    window.parent.postMessage(JSON.stringify({
      type: this.keyboardShortcutMessageType,
      key: event.key,
      code: event.code,
      shiftKey: !!event.shiftKey,
      altKey: !!event.altKey,
      ctrlKey: !!event.ctrlKey,
      metaKey: !!event.metaKey,
      plain: !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey,
      source: 'IFRAME',
      sequence: `IFRAME-${Date.now()}-${Math.random()}`
    }), '*');
  }

  // Keeps normal typing and browser-reserved shortcuts out of the iframe bridge.
  private shouldBridgeKeyboardEvent(event: KeyboardEvent): boolean {
    if (event.defaultPrevented || event.ctrlKey || event.altKey || event.metaKey || event.isComposing) {
      return false;
    }

    // Relay app shortcut candidates only: navigation keys plus plain letters/digits.
    return event.key === KeyboardShortcuts.ESCAPE
      || event.key === KeyboardShortcuts.TAB
      || event.key === KeyboardShortcuts.QUESTION_MARK
      || event.code === KeyboardShortcuts.SLASH
      || event.code?.startsWith('Key')
      || event.code?.startsWith('Digit');
  }
}
